/**
 * 📦 Package Service — Application Layer
 *
 * Use Cases:
 *   - قائمة البكجات لقاعة (live stream)
 *   - إضافة بكج (مع منع تكرار الهوية والسعة)
 *   - تعديل بكج
 *   - حذف بكج
 *   - Smart resolver (يلقى بكج مناسب لتركيبة معيّنة)
 *
 * بعد كل تعديل، يعيد حساب بيانات القاعة المشتقة من الباقات تلقائياً.
 */

import { Package, type CreatePackageProps } from '@domain/entities/Package';
import {
  packageDateScopesConflict,
  packageVariantRangesConflict,
  packageVariantsDuplicate,
} from '@domain/services/PackageConflictPolicy';
import {
  bookingPackageSpecificity,
  packageContainsBookingGuestCounts,
  packageMatchesBookingCriteria,
} from '@domain/services/PackageBookingMatchPolicy';
import { Money } from '@domain/value-objects/Money';
import {
  SectionModeLabels,
  SectionModeIcons,
  DayTypeLabels,
  SeasonLabels,
  SeasonIcons,
  type SectionModeValue,
  type DayTypeValue,
  type SeasonValue,
} from '@domain/value-objects/PackageEnums';
import type {
  IncludedServices,
  MenIncludedServices,
  WomenIncludedServices,
} from '@domain/value-objects/IncludedServices';
import { packageRepository } from '@infrastructure/firebase/repositories/FirestorePackageRepository';
import {
  eventDateClassifier,
  type DateClassification,
} from './EventDateClassifier';
import {
  ConflictError,
  NotFoundError,
} from '@domain/errors/DomainErrors';
import type { IPackageRepository } from '@domain/repositories/IPackageRepository';

export interface CreatePackageInput {
  venueId: string;
  name: string;
  priceSAR: number;
  capacityMin?: number;
  capacityMax?: number;
  capacityMenMin?: number;
  capacityMenMax?: number;
  capacityWomenMin?: number;
  capacityWomenMax?: number;
  sectionMode: SectionModeValue;
  dayType: DayTypeValue;
  season: SeasonValue;
  hasIncludedServices: boolean;
  includedServicesText?: string;
  includedServices?: IncludedServices;
  includedServicesMenText?: string;
  includedServicesMen?: MenIncludedServices;
  includedServicesWomenText?: string;
  includedServicesWomen?: WomenIncludedServices;
  notes?: string;
}

export interface UpdatePackageInput {
  name?: string;
  priceSAR?: number;
  capacityMin?: number;
  capacityMax?: number;
  capacityMenMin?: number;
  capacityMenMax?: number;
  capacityWomenMin?: number;
  capacityWomenMax?: number;
  sectionMode?: SectionModeValue;
  dayType?: DayTypeValue;
  season?: SeasonValue;
  hasIncludedServices?: boolean;
  includedServicesText?: string;
  includedServices?: IncludedServices;
  includedServicesMenText?: string;
  includedServicesMen?: MenIncludedServices;
  includedServicesWomenText?: string;
  includedServicesWomen?: WomenIncludedServices;
  notes?: string;
}

class PackageService {
  constructor(
    private repository: IPackageRepository = packageRepository
  ) {}

  /**
   * 📋 قائمة البكجات لقاعة
   */
  async list(venueId: string): Promise<Package[]> {
    return this.repository.listByVenue(venueId);
  }

  /**
   * 📅 Returns packages that can actually be booked for the completed form.
   *
   * Date identity is derived by EventDateClassifier. Section and services are
   * exact user choices. The package must also fit the entered guest count.
   * `all` is supported only as a wildcard for dayType and season.
   */
  async listMatchingForDate(
    venueId: string,
    date: string | Date,
    criteria: {
      sectionMode: SectionModeValue;
      hasIncludedServices: boolean;
      guestCount: number;
      guestCountMen?: number;
      guestCountWomen?: number;
    }
  ): Promise<{ classification: DateClassification; packages: Package[] }> {
    await eventDateClassifier.ensureLoaded();
    const classification = eventDateClassifier.classify(date);

    const dateScopes = [
      { dayType: classification.dayType, season: classification.season },
      { dayType: 'all' as const, season: classification.season },
      { dayType: classification.dayType, season: 'all' as const },
      { dayType: 'all' as const, season: 'all' as const },
    ];
    const queried = await Promise.all(
      dateScopes.map((scope) =>
        this.repository.query(venueId, {
          ...scope,
          sectionMode: criteria.sectionMode,
          hasIncludedServices: criteria.hasIncludedServices,
        })
      )
    );
    const all = [...new Map(
      queried.flat().map((pkg) => [pkg.key, pkg] as const)
    ).values()];

    const matched = all
      .filter((pkg) =>
        packageMatchesBookingCriteria(pkg, {
          ...criteria,
          dayType: classification.dayType,
          season: classification.season,
        })
      )
      .sort((a, b) => {
        const aExact = packageContainsBookingGuestCounts(a, criteria);
        const bExact = packageContainsBookingGuestCounts(b, criteria);
        if (aExact !== bExact) return aExact ? -1 : 1;

        const diff =
          bookingPackageSpecificity(b) - bookingPackageSpecificity(a);
        if (diff !== 0) return diff;
        return a.props.price.toSAR() - b.props.price.toSAR();
      });

    return { classification, packages: matched };
  }

  /**
   * 🔴 Live stream — قائمة البكجات تتحدّث تلقائياً
   */
  subscribe(
    venueId: string,
    callback: (packages: Package[]) => void,
    onError?: (err: Error) => void
  ): () => void {
    return this.repository.subscribeToVenue(venueId, callback, onError);
  }

  /**
   * 🔍 بكج واحد بـ key
   */
  async getByKey(venueId: string, key: string): Promise<Package> {
    const pkg = await this.repository.getByKey(venueId, key);
    if (!pkg) {
      throw new NotFoundError('البكج غير موجود', { venueId, key });
    }
    return pkg;
  }

  /**
   * ➕ إنشاء بكج جديد (مع منع duplicate ومنع التعارض)
   *
   * 🛡️ Conflict Prevention:
   *   - duplicate exact (نفس الهوية والسعة): ConflictError
   *   - نطاقا يوم/موسم مختلفان ومتداخلان: ConflictError
   *   - نطاق اليوم/الموسم نفسه مسموح مع قسم أو خدمات أو سعة مختلفة
   *     مثال: لو موجود `both_all_all_no` لا يقبل `men_weekday_school_yes`،
   *           لكنه يقبل `men_all_all_yes`.
   */
  async create(input: CreatePackageInput): Promise<Package> {
    const props: CreatePackageProps = {
      venueId: input.venueId,
      name: input.name,
      price: Money.fromSAR(input.priceSAR),
      ...(input.capacityMin !== undefined
        ? { capacityMin: input.capacityMin }
        : {}),
      ...(input.capacityMax !== undefined
        ? { capacityMax: input.capacityMax }
        : {}),
      ...(input.capacityMenMin !== undefined
        ? { capacityMenMin: input.capacityMenMin }
        : {}),
      ...(input.capacityMenMax !== undefined
        ? { capacityMenMax: input.capacityMenMax }
        : {}),
      ...(input.capacityWomenMin !== undefined
        ? { capacityWomenMin: input.capacityWomenMin }
        : {}),
      ...(input.capacityWomenMax !== undefined
        ? { capacityWomenMax: input.capacityWomenMax }
        : {}),
      sectionMode: input.sectionMode,
      dayType: input.dayType,
      season: input.season,
      hasIncludedServices: input.hasIncludedServices,
      ...(input.includedServicesText !== undefined
        ? { includedServicesText: input.includedServicesText }
        : {}),
      ...(input.includedServices !== undefined
        ? { includedServices: input.includedServices }
        : {}),
      ...(input.includedServicesMenText !== undefined
        ? { includedServicesMenText: input.includedServicesMenText }
        : {}),
      ...(input.includedServicesMen !== undefined
        ? { includedServicesMen: input.includedServicesMen }
        : {}),
      ...(input.includedServicesWomenText !== undefined
        ? { includedServicesWomenText: input.includedServicesWomenText }
        : {}),
      ...(input.includedServicesWomen !== undefined
        ? { includedServicesWomen: input.includedServicesWomen }
        : {}),
      ...(input.notes !== undefined ? { notes: input.notes } : {}),
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    const pkg = Package.create(props);

    // 🛡️ 1) منع duplicate (نفس الهوية والسعة بالضبط)
    const existingPackages = await this.repository.listByVenue(input.venueId);
    const duplicate = existingPackages.find(
      (existing) => packageVariantsDuplicate(existing.props, pkg.props)
    );
    if (duplicate) {
      throw new ConflictError('هذه التركيبة والسعة موجودتان مسبقاً', {
        key: pkg.key,
        existingKey: duplicate.key,
      });
    }

    const rangeConflict = existingPackages.find((existing) =>
      packageVariantRangesConflict(existing.props, pkg.props)
    );
    if (rangeConflict) {
      throw new ConflictError(
        'يتداخل نطاق السعة مع باقة موجودة لها نفس القسم واليوم والموسم والخدمات',
        { key: pkg.key, existingKey: rangeConflict.key }
      );
    }

    // 🛡️ 2) منع التعارض مع البكجات الموجودة (overlap)
    const conflicts = await this.detectConflicts(input.venueId, {
      dayType: input.dayType,
      season: input.season,
    });

    if (conflicts.length > 0) {
      throw new ConflictError(
        formatConflictMessage(conflicts),
        { conflicts: conflicts.map((c) => c.summary) }
      );
    }

    await this.repository.create(pkg);

    return pkg;
  }

  /**
   * 🔍 كشف التعارض — يلقى البكجات اللي تتداخل مع التركيبة الجديدة
   *
   * Logic:
   *   اليوم والموسم يحددهما تاريخ المناسبة تلقائياً، لذلك نمنع وجود نطاقي
   *   تاريخ مختلفين يغطيان المناسبة نفسها، مهما اختلف القسم أو الخدمات.
   *
   *   يمكن تكرار نطاق اليوم والموسم نفسه مع تغيير القسم أو الخدمات أو السعة.
   *
   *   مثال على overlap:
   *     موجود: both_all_all_no
   *     جديد: both_weekday_school_yes    → تعارض رغم اختلاف الخدمات
   *     جديد: men_all_all_yes            → مسموح؛ نطاق التاريخ نفسه
   *
   *     موجود: both_weekend_summer_no
   *     جديد: men_weekend_summer_yes     → مسموح؛ نطاق التاريخ نفسه
   *     جديد: women_weekend_all_no       → تعارض؛ نطاقان متداخلان ومختلفان
   */
  async detectConflicts(
    venueId: string,
    target: {
      dayType: DayTypeValue;
      season: SeasonValue;
    }
  ): Promise<
    Array<{
      pkg: Package;
      summary: string;
      reason: string;
    }>
  > {
    const all = await this.repository.listByVenue(venueId);
    const conflicts: Array<{ pkg: Package; summary: string; reason: string }> = [];

    for (const pkg of all) {
      if (packageDateScopesConflict(pkg.props, target)) {
        conflicts.push({
          pkg,
          summary: summarizePackage(pkg),
          reason: describeDateScopeConflict(pkg.props, target),
        });
      }
    }

    return conflicts;
  }

  /**
   * ✏️ تعديل بكج
   */
  async update(
    venueId: string,
    oldKey: string,
    input: UpdatePackageInput
  ): Promise<Package> {
    const existing = await this.getByKey(venueId, oldKey);

    const updates: Parameters<Package['update']>[0] = {};
    if (input.name !== undefined) updates.name = input.name;
    if (input.priceSAR !== undefined) {
      updates.price = Money.fromSAR(input.priceSAR);
    }
    const nextSectionMode = input.sectionMode ?? existing.props.sectionMode;
    if (nextSectionMode === 'both') {
      updates.capacityMin = input.capacityMin;
      updates.capacityMax = input.capacityMax;
      updates.capacityMenMin = input.capacityMenMin;
      updates.capacityMenMax = input.capacityMenMax;
      updates.capacityWomenMin = input.capacityWomenMin;
      updates.capacityWomenMax = input.capacityWomenMax;
    } else {
      updates.capacityMin = input.capacityMin;
      updates.capacityMax = input.capacityMax;
      updates.capacityMenMin = undefined;
      updates.capacityMenMax = undefined;
      updates.capacityWomenMin = undefined;
      updates.capacityWomenMax = undefined;
    }
    if (input.sectionMode !== undefined) {
      updates.sectionMode = input.sectionMode;
    }
    if (input.dayType !== undefined) updates.dayType = input.dayType;
    if (input.season !== undefined) updates.season = input.season;
    if (input.hasIncludedServices !== undefined) {
      updates.hasIncludedServices = input.hasIncludedServices;
    }
    if (input.includedServicesText !== undefined) {
      updates.includedServicesText = input.includedServicesText;
    }
    if (input.includedServices !== undefined) {
      updates.includedServices = input.includedServices;
    }
    if (input.includedServicesMenText !== undefined) {
      updates.includedServicesMenText = input.includedServicesMenText;
    }
    if (input.includedServicesMen !== undefined) {
      updates.includedServicesMen = input.includedServicesMen;
    }
    if (input.includedServicesWomenText !== undefined) {
      updates.includedServicesWomenText = input.includedServicesWomenText;
    }
    if (input.includedServicesWomen !== undefined) {
      updates.includedServicesWomen = input.includedServicesWomen;
    }
    if (input.hasIncludedServices === false) {
      updates.includedServicesText = undefined;
      updates.includedServices = undefined;
      updates.includedServicesMenText = undefined;
      updates.includedServicesMen = undefined;
      updates.includedServicesWomenText = undefined;
      updates.includedServicesWomen = undefined;
    } else if (nextSectionMode === 'both') {
      updates.includedServicesText = undefined;
      updates.includedServices = undefined;
    } else {
      updates.includedServicesMenText = undefined;
      updates.includedServicesMen = undefined;
      updates.includedServicesWomenText = undefined;
      updates.includedServicesWomen = undefined;
    }
    if (input.notes !== undefined) updates.notes = input.notes;

    const updated = existing.update(updates);

    // 🛡️ لو الـ key اتغيّر، تأكد إنه ما يصير duplicate ولا تعارض
    if (updated.key !== oldKey) {
      // 1) منع duplicate بالهوية والسعة، مع تجاهل المستند الجاري تعديله
      const duplicate = (await this.repository.listByVenue(venueId)).find(
        (candidate) =>
          candidate.key !== oldKey &&
          packageVariantsDuplicate(candidate.props, updated.props)
      );
      if (duplicate) {
        throw new ConflictError(
          'لا يمكن التعديل — التركيبة والسعة الجديدتان موجودتان مسبقاً',
          { oldKey, newKey: updated.key, existingKey: duplicate.key }
        );
      }

      const rangeConflict = (await this.repository.listByVenue(venueId)).find(
        (candidate) =>
          candidate.key !== oldKey &&
          packageVariantRangesConflict(candidate.props, updated.props)
      );
      if (rangeConflict) {
        throw new ConflictError(
          'لا يمكن التعديل — نطاق السعة يتداخل مع باقة موجودة',
          { oldKey, newKey: updated.key, existingKey: rangeConflict.key }
        );
      }

      // 2) منع التعارض مع بكجات أخرى (نتجاهل الـ oldKey لأنه راح ينحذف)
      const conflicts = (
        await this.detectConflicts(venueId, {
          dayType: updated.props.dayType,
          season: updated.props.season,
        })
      ).filter((c) => c.pkg.key !== oldKey);

      if (conflicts.length > 0) {
        throw new ConflictError(formatConflictMessage(conflicts), {
          conflicts: conflicts.map((c) => c.summary),
        });
      }

      // نقل ذري: لا تُكتب الوجهة ولا يُحذف المصدر عند وجود تصادم
      await this.repository.move(venueId, oldKey, updated);
    } else {
      await this.repository.save(updated);
    }

    return updated;
  }

  /**
   * 💰 (Phase 5) تعديل أسعار جماعي للبكجات
   *
   * Modes:
   *   - 'percentage_increase': زيادة بنسبة % (مثلاً 10% = 1.1x)
   *   - 'percentage_decrease': تخفيض بنسبة % (مثلاً 10% = 0.9x)
   *   - 'fixed_add': إضافة مبلغ ثابت لكل بكج
   *   - 'fixed_subtract': خصم مبلغ ثابت من كل بكج
   *   - 'set_min_price': تعيين سعر أدنى (لكل البكجات اللي تحت هذا السعر)
   *
   * @param venueId
   * @param mode
   * @param amount نسبة (0-100) أو مبلغ بالـ SAR
   * @param filter اختياري: dayType / season / sectionMode
   */
  async bulkUpdatePrices(
    venueId: string,
    mode:
      | 'percentage_increase'
      | 'percentage_decrease'
      | 'fixed_add'
      | 'fixed_subtract'
      | 'set_min_price',
    amount: number,
    filter?: {
      dayType?: DayTypeValue;
      season?: SeasonValue;
      sectionMode?: SectionModeValue;
    }
  ): Promise<{
    updated: number;
    skipped: number;
    totalDelta: number; // الفرق الإجمالي في الأسعار
  }> {
    if (amount < 0) {
      throw new Error('القيمة يجب أن تكون موجبة');
    }
    if (
      (mode === 'percentage_increase' || mode === 'percentage_decrease') &&
      amount > 100
    ) {
      throw new Error('النسبة لا يجب أن تتجاوز 100%');
    }

    let packages = await this.list(venueId);

    // Apply filter
    if (filter?.dayType) {
      packages = packages.filter((p) => p.props.dayType === filter.dayType);
    }
    if (filter?.season) {
      packages = packages.filter((p) => p.props.season === filter.season);
    }
    if (filter?.sectionMode) {
      packages = packages.filter((p) => p.props.sectionMode === filter.sectionMode);
    }

    let skipped = 0;
    let totalDelta = 0;
    const packagesToUpdate: Package[] = [];

    // 🚀 1️⃣ احسب الأسعار الجديدة في الذاكرة (بدون أي Firestore call)
    for (const pkg of packages) {
      try {
        const oldPrice = pkg.props.price.toSAR();
        let newPrice: number;

        switch (mode) {
          case 'percentage_increase':
            newPrice = Math.round(oldPrice * (1 + amount / 100));
            break;
          case 'percentage_decrease':
            newPrice = Math.round(oldPrice * (1 - amount / 100));
            break;
          case 'fixed_add':
            newPrice = oldPrice + amount;
            break;
          case 'fixed_subtract':
            newPrice = Math.max(0, oldPrice - amount);
            break;
          case 'set_min_price':
            if (oldPrice >= amount) {
              skipped++;
              continue;
            }
            newPrice = amount;
            break;
          default:
            skipped++;
            continue;
        }

        if (newPrice <= 0 || newPrice === oldPrice) {
          skipped++;
          continue;
        }

        // ✨ إنشاء النسخة المحدّثة في الذاكرة فقط
        const updatedPkg = pkg.update({ price: Money.fromSAR(newPrice) });
        packagesToUpdate.push(updatedPkg);
        totalDelta += newPrice - oldPrice;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[bulkUpdatePrices] failed for', pkg.key, err);
        skipped++;
      }
    }

    // 🚀 2️⃣ احفظهم في batch واحد (10x أسرع من الـ sequential)
    if (packagesToUpdate.length > 0) {
      await this.repository.saveBatch(packagesToUpdate);

    }

    return {
      updated: packagesToUpdate.length,
      skipped,
      totalDelta,
    };
  }

  async delete(venueId: string, key: string): Promise<void> {
    await this.repository.delete(venueId, key);
  }

  /**
   * 🎯 Smart Resolver — يلقى أفضل بكج لتركيبة معيّنة
   *
   * Priority:
   *   1. exact match (sectionMode + dayType + season + hasIncludedServices)
   *   2. matches with 'all' fallbacks
   *   3. أعلى matchScore
   */
  async findBestMatch(
    venueId: string,
    filter: {
      sectionMode: SectionModeValue;
      dayType: DayTypeValue;
      season: SeasonValue;
      hasIncludedServices: boolean;
    }
  ): Promise<Package | null> {
    const all = await this.repository.listByVenue(venueId);

    let bestMatch: Package | null = null;
    let bestScore = -1;

    for (const pkg of all) {
      const score = pkg.matchScore(filter);
      if (score > bestScore) {
        bestScore = score;
        bestMatch = pkg;
      }
    }

    return bestMatch;
  }

  /**
   * 🗑️ حذف كل البكجات لقاعة (للـ admin)
   */
  async deleteAll(venueId: string): Promise<number> {
    const packages = await this.repository.listByVenue(venueId);
    for (const pkg of packages) {
      await this.repository.delete(venueId, pkg.key);
    }
    return packages.length;
  }
}

// ═══════════════════════════════════════════════════
// 🎨 Helpers — Conflict messages
// ═══════════════════════════════════════════════════

function summarizePackage(pkg: Package): string {
  const p = pkg.props;
  const parts = [
    `📦 ${p.name}`,
    `${SectionModeIcons[p.sectionMode]} ${SectionModeLabels[p.sectionMode]}`,
    `📆 ${DayTypeLabels[p.dayType]}`,
    `${SeasonIcons[p.season]} ${SeasonLabels[p.season]}`,
    p.hasIncludedServices ? '✨ شامل خدمات' : '💎 بدون خدمات',
  ];
  return parts.join(' · ');
}

function describeDateScopeConflict(
  existing: { dayType: DayTypeValue; season: SeasonValue },
  target: { dayType: DayTypeValue; season: SeasonValue }
): string {
  const existingScope = formatDateScope(existing);
  const targetScope = formatDateScope(target);

  if (existing.dayType === 'all' && existing.season === 'all') {
    return (
      'الباقة الموجودة تشمل كل الأيام وكل المواسم، لذلك لا يمكن إضافة ' +
      `النطاق «${targetScope}». أضف باقة أخرى بنطاق «كل الأيام · كل المواسم» ` +
      'وغيّر القسم أو الخدمات فقط.'
    );
  }

  if (target.dayType === 'all' && target.season === 'all') {
    return (
      'الباقة الجديدة ستشمل كل الأيام وكل المواسم، لكنها تتداخل مع ' +
      `النطاق الموجود «${existingScope}». احذف أو عدّل الباقات ذات ` +
      'النطاق المحدد أولاً.'
    );
  }

  return (
    `النطاق الجديد «${targetScope}» يتداخل مع النطاق الموجود ` +
    `«${existingScope}». استخدم نطاق اليوم والموسم نفسه وغيّر القسم أو ` +
    'الخدمات فقط، أو اختر نطاقاً لا يتداخل معه.'
  );
}

function formatDateScope(scope: {
  dayType: DayTypeValue;
  season: SeasonValue;
}): string {
  return `${DayTypeLabels[scope.dayType]} · ${SeasonLabels[scope.season]}`;
}

function formatConflictMessage(
  conflicts: Array<{ pkg: Package; summary: string; reason: string }>
): string {
  if (conflicts.length === 1) {
    const c = conflicts[0]!;
    return (
      `لا يمكن حفظ الباقة لأن نطاق اليوم والموسم يتداخل مع باقة موجودة:\n\n` +
      `   "${c.summary}"\n   بسعر ${c.pkg.props.price.toSAR().toLocaleString('ar-SA')} ر.س\n\n` +
      `${c.reason}`
    );
  }

  const items = conflicts
    .map((c) => `   • "${c.summary}" — ${c.reason}`)
    .join('\n');
  return (
    `لا يمكن حفظ الباقة؛ نطاق اليوم والموسم يتداخل مع ${conflicts.length} باقات موجودة:\n\n` +
    items +
    `\n\nعدّل نطاق اليوم والموسم أو عدّل الباقات المتعارضة أولاً.`
  );
}

export const packageService = new PackageService();
